import { StyleSheet, Text, View } from 'react-native';

import { colors, fonts, fontSizes, spacing } from '@/constants/theme';
import type { LeaderboardEntry } from '@/types/leaderboard';

import { Avatar } from './Avatar';
import { MoneyDisplay } from './MoneyDisplay';
import { formatMoneyForA11y } from './PodiumCard';

// Soft Electric Green used to tint the signed-in user's row. Same drift
// class as the home stat-accent (`'rgba(120, 193, 68, 0.18)'`); we use a
// lighter alpha here because the row is already on a Card surface.
// Documented in `docs/screens/leaderboard.md` Drift table.
const ME_ROW_BG = 'rgba(120, 193, 68, 0.10)';

export interface LeaderboardRowProps {
  entry: LeaderboardEntry;
  /** Highlight as the signed-in user with a soft tint + accent strip. */
  isMe?: boolean;
  /** Whether to render a hairline divider beneath this row. */
  divider?: boolean;
  testID?: string;
}

export function LeaderboardRow({ entry, isMe, divider, testID }: LeaderboardRowProps) {
  const initials = initialsFor(entry.display_name);

  return (
    <View
      testID={testID}
      accessible
      accessibilityRole="text"
      accessibilityLabel={[
        `Rank ${entry.rank}`,
        entry.display_name,
        `${formatMoneyForA11y(entry.month_cents)} from referrals`,
        isMe ? "that's you" : null,
      ]
        .filter(Boolean)
        .join(', ')}
      style={[
        styles.row,
        isMe && styles.rowMe,
        divider && styles.rowDivider,
      ]}
    >
      <Text
        style={[styles.rank, isMe ? styles.rankMe : styles.rankDefault]}
        numberOfLines={1}
        adjustsFontSizeToFit
        minimumFontScale={0.6}
      >
        {entry.rank}
      </Text>
      <Avatar size={36} initials={initials} tone={isMe ? 'electric' : 'green'} />
      <View style={styles.body}>
        <Text style={styles.name} numberOfLines={1} ellipsizeMode="tail">
          {entry.display_name}
        </Text>
      </View>
      <MoneyDisplay cents={entry.month_cents} size="sm" />
    </View>
  );
}

function initialsFor(displayName: string): string {
  const parts = displayName.trim().split(/\s+/);
  const first = parts[0];
  const second = parts[1];
  if (first && second) {
    const firstChar = [...first][0] ?? '';
    const secondChar = [...second][0] ?? '';
    return (firstChar + secondChar).toUpperCase();
  }
  if (first) {
    const chars = [...first].slice(0, 2).join('');
    return chars.toUpperCase();
  }
  return '?';
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.base,
    backgroundColor: 'transparent',
  },
  rowMe: {
    backgroundColor: ME_ROW_BG,
  },
  rowDivider: {
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  rank: {
    // Druk Wide digits are wide: 32 fit "1x" ranks but wrapped "20".
    width: 44,
    textAlign: 'center',
    fontFamily: fonts.numeric,
    fontSize: 18, // matches handoff `leaderboard.jsx:241`; not in fontSizes scale
    lineHeight: 22,
  },
  rankDefault: {
    color: colors.textMuted,
  },
  rankMe: {
    color: colors.deepGreen,
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.sm,
    color: colors.deepGreen,
  },
});
